import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
    CatalogueModel, NewsletterViewModel, NewsletterInfo, NewsletterEditViewModel, SavePromoViewModel
    , NewsletterSectionResponseModel, NewsletterPromo, NewsletterSection
} from '../models/catalogue-view.model';
import { catchError } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs';
import { ApiResponse } from '../models/api-response.model';
import { AuthService } from './auth.service';
import { Subject } from 'rxjs';


@Injectable()
export class CatalogueService {

    public static colorList: any[] = [
        { value: 'newsletter-000000', style: '#000000' },
        { value: 'newsletter-FFFFFF', style: '#FFFFFF' },
        { value: 'newsletter-FF0000', style: '#FF0000' },
        { value: 'newsletter-00FF00', style: '#00FF00' },
        { value: 'newsletter-0000FF', style: '#0000FF' },
        { value: 'newsletter-800000', style: '#800000' },
        { value: 'newsletter-008000', style: '#008000' },
        { value: 'newsletter-000080', style: '#000080' },
        { value: 'newsletter-808000', style: '#808000' },
        { value: 'newsletter-800080', style: '#800080' },
        { value: 'newsletter-008080', style: '#008080' },
        { value: 'newsletter-C0C0C0', style: '#C0C0C0' },
        { value: 'newsletter-808080', style: '#808080' },
        { value: 'newsletter-9999FF', style: '#9999FF' },
        { value: 'newsletter-993366', style: '#993366' },
        { value: 'newsletter-FFFFCC', style: '#FFFFCC' },
        { value: 'newsletter-CCFFFF', style: '#CCFFFF' },
        { value: 'newsletter-660066', style: '#660066' },
        { value: 'newsletter-FF8080', style: '#FF8080' },
        { value: 'newsletter-0066CC', style: '#0066CC' },
        { value: 'newsletter-CCCCFF', style: '#CCCCFF' },
        { value: 'newsletter-00CCFF', style: '#00CCFF' },
        { value: 'newsletter-CCFFCC', style: '#CCFFCC' },
        { value: 'newsletter-FFFF99', style: '#FFFF99' },
        { value: 'newsletter-99CCFF', style: '#99CCFF' },
        { value: 'newsletter-FF99CC', style: '#FF99CC' },
        { value: 'newsletter-CC99FF', style: '#CC99FF' },
        { value: 'newsletter-FFCC99', style: '#FFCC99' },
        { value: 'newsletter-3366FF', style: '#3366FF' },
        { value: 'newsletter-33CCCC', style: '#33CCCC' },
        { value: 'newsletter-99CC00', style: '#99CC00' },
        { value: 'newsletter-FFCC00', style: '#FFCC00' },
        { value: 'newsletter-FF9900', style: '#FF9900' },
        { value: 'newsletter-FF6600', style: '#FF6600' },
        { value: 'newsletter-666699', style: '#666699' },
        { value: 'newsletter-969696', style: '#969696' },
        { value: 'newsletter-003366', style: '#003366' },
        { value: 'newsletter-339966', style: '#339966' },
        { value: 'newsletter-003300', style: '#003300' },
        { value: 'newsletter-333300', style: '#333300' },
        { value: 'newsletter-993300', style: '#993300' },
        { value: 'newsletter-333399', style: '#333399' },
        { value: 'newsletter-333333', style: '#333333' }
    ];

    base_url = 'api/Catalogue';

    newsId: string = "";

    editNewsletterInfo: string = "";

    editNewsletterSectionId: string = "";

    watchBlock: NewsletterSectionResponseModel = {} as NewsletterSectionResponseModel;
    watchColor: string = "";

    private watchSubject = new BehaviorSubject<string>(this.newsId);
    currentWatch = this.watchSubject.asObservable();

    private watchEditNewsletterSubject = new BehaviorSubject<NewsletterEditViewModel>({} as NewsletterEditViewModel);
    currentEditNewsletterWatch = this.watchEditNewsletterSubject.asObservable();

    private watchSectionIdSubject = new BehaviorSubject<string>(this.editNewsletterSectionId);
    currentSectionIdWatch = this.watchSectionIdSubject.asObservable();

    private watchBlockSubject = new Subject<NewsletterSectionResponseModel>();
    currentBlockWatch = this.watchBlockSubject.asObservable();

    private watchColorSubject = new BehaviorSubject<string>(this.watchColor);
    currentColorWatch = this.watchColorSubject.asObservable();

    constructor(
        private http: HttpClient,
        private authService: AuthService
    ) { }

    monitorNewsIdChange(newsId: string): void {
        this.watchSubject.next(newsId);
    }

    monitorEditNewsletterInfoChange(info: NewsletterEditViewModel): void {
        this.watchEditNewsletterSubject.next(info);
    }

    monitorSectionIdChange(sectionId: string): void {
        this.watchSectionIdSubject.next(sectionId);
    }

    monitorBlockChange(watchBlock: NewsletterSectionResponseModel): void {
        this.watchBlockSubject.next(watchBlock);
    }

    monitorColorChange(color: string): void {
        this.watchColorSubject.next(color);
    }

    getAllCatalogues(): Observable<CatalogueModel> {
        return this.http.get<CatalogueModel>(this.base_url + '/getallcatalogues')
            .pipe(catchError(this.authService.handleError));
    }

    getNewsletterInfo(newsId: string): Observable<NewsletterInfo> {
        return this.http.get<NewsletterInfo>(this.base_url + '/getnewsletterinfo?newsId=' + newsId)
            .pipe(catchError(this.authService.handleError));
    }

    getNewsletterView(newsId: string): Observable<NewsletterViewModel> {
        return this.http.get<NewsletterViewModel>(this.base_url + '/getnewsletterview?newsId=' + newsId)
            .pipe(catchError(this.authService.handleError));
    }

    exportExcelForCatalogue(newsId: string): Observable<any> {
        return this.http.get('/api/ExportExcel/exportnewslettertoexcel?newsId=' + newsId,
        { responseType: 'blob', headers: {'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'} })
            .pipe(catchError(this.authService.handleError));
    }

    sendAllNewsletters(billTo: string): Observable<any> {
        return this.http.get(this.base_url + '/sendallnewsletters?billTo=' + billTo)
            .pipe(catchError(this.authService.handleError));
    }

    deleteNewsletter(newsId: string): Observable<ApiResponse> {
        return this.http.get<ApiResponse>(this.base_url + '/deletenewsletter?newsId=' + newsId)
            .pipe(catchError(this.authService.handleError));
    }

    updateNewsletterInfo(newsId: string, actionType: string): Observable<NewsletterInfo> {
        return this.http.get<NewsletterInfo>(this.base_url + '/updatenewsletterinfo?newsId=' + newsId + '&actionType=' + actionType)
            .pipe(catchError(this.authService.handleError));
    }
 
    createNewNewsletter(): Observable<NewsletterInfo> {
        return this.http.get<NewsletterInfo>(this.base_url + '/newnewsletters')
            .pipe(catchError(this.authService.handleError));
    }

    newsletterEdit(newsId: string): Observable<NewsletterEditViewModel> {
        return this.http.get<NewsletterEditViewModel>(this.base_url + '/editnewsletter?newsId=' + newsId)
            .pipe(catchError(this.authService.handleError));
    }

    getPromo(newsId: string, sectionId: string): Observable<NewsletterPromo> {
        return this.http.get<NewsletterPromo>(this.base_url + '/getpromo?newsId=' + newsId + '&sectionId=' + sectionId)
            .pipe(catchError(this.authService.handleError));
    }

    getDataFeedDownloadLink(): Observable<string> {
        return this.http.get<string>(this.base_url + '/GetDataFeedDownloadLink')
        .pipe(catchError(this.authService.handleError));
    }

    getDataFeedJSONDownloadLink(): Observable<string> {
        return this.http.get<string>(this.base_url + '/GetDataFeedJSONDownloadLink')
        .pipe(catchError(this.authService.handleError));
    }

    getDataFeedXMLDownloadLink(): Observable<string> {
        return this.http.get<string>(this.base_url + '/GetDataFeedXMLDownloadLink')
        .pipe(catchError(this.authService.handleError));
    }

    deleteNewsletterPromo(newsId: string, sectionId: string): Observable<ApiResponse> {
        return this.http.get<ApiResponse>(this.base_url + '/deletepromo?newsId=' + newsId + '&sectionId=' + sectionId)
            .pipe(catchError(this.authService.handleError));
    }

    // saveHeader(newsId: string, header: string, headerType: string): Observable<NewsletterInfo> {
    //     return this.http.get<NewsletterInfo>(this.base_url
    //         + '/saveheader?newsId=' + newsId
    //         + '&' + headerType + '=' + header
    //     )
    //         .pipe(catchError(this.authService.handleError));
    // }

    newSaveHeader(newsId: string, header: string, headerType: string, license: string): Observable<NewsletterInfo> {
        return this.http.get<NewsletterInfo>(this.base_url
            + '/saveheader?newsId=' + newsId
            + '&' + headerType + '=' + header 
            + '&license=' + license
        )
            .pipe(catchError(this.authService.handleError));
    }

    // updateHeader(model: NewsletterEditViewModel): Observable<NewsletterInfo> {
    //     return this.http.post<NewsletterInfo>(this.base_url + '/updateheader?newsId=' + model.newsId, model)
    //         .pipe(catchError(this.authService.handleError));
    // }

    savePromo(model: NewsletterPromo): Observable<NewsletterSectionResponseModel> {
        return this.http.post<NewsletterSectionResponseModel>(this.base_url + '/savepromo', model)
            .pipe(catchError(this.authService.handleError));
    }

    addPromo(newsId: string): Observable<NewsletterSectionResponseModel> {
        return this.http.get<NewsletterSectionResponseModel>(this.base_url + '/addpromo?newsId=' + newsId)
            .pipe(catchError(this.authService.handleError));
    }

    saveProducts(model: NewsletterSection): Observable<NewsletterSectionResponseModel> {
        return this.http.post<NewsletterSectionResponseModel>(this.base_url + '/saveproducts', model)
            .pipe(catchError(this.authService.handleError));
    }

    savePromoImage(sectionId: string, type: string, image: any): Observable<NewsletterSectionResponseModel> {
        return this.http.post<NewsletterSectionResponseModel>(this.base_url + '/savepromoimage?sectionId=' + sectionId + '&type=' + type, image)
            .pipe(catchError(this.authService.handleError));
    }

    addSection(newsId: string): Observable<NewsletterSectionResponseModel> {
        return this.http.get<NewsletterSectionResponseModel>(this.base_url + '/addsection?newsId=' + newsId)
            .pipe(catchError(this.authService.handleError));
    }

    getSections(newsId: string): Observable<NewsletterInfo> {
        return this.http.get<NewsletterInfo>(this.base_url + '/getsections?newsId=' + newsId)
            .pipe(catchError(this.authService.handleError));
    }

    getSection(sectionId: string): Observable<NewsletterSectionResponseModel> {
        return this.http.get<NewsletterSectionResponseModel>(this.base_url + '/getsection?sectionId=' + sectionId)
            .pipe(catchError(this.authService.handleError));
    }

    deleteSection(newsId: string, sectionId: string): Observable<ApiResponse> {
        return this.http.get<ApiResponse>(this.base_url + '/deletesection?newsId=' + newsId + '&sectionId=' + sectionId)
            .pipe(catchError(this.authService.handleError));
    }

    deleteProducts(newsId: string, sectionId: string): Observable<ApiResponse> {
        return this.http.get<ApiResponse>(this.base_url + '/deleteproducts?newsId=' + newsId + '&sectionId=' + sectionId)
            .pipe(catchError(this.authService.handleError));
    }

    deleteLink(newsId: string, sectionId: string): Observable<ApiResponse> {
        return this.http.get<ApiResponse>(this.base_url + '/deletelink?newsId=' + newsId + '&sectionId=' + sectionId)
            .pipe(catchError(this.authService.handleError));
    }

    saveSection(model: NewsletterSection): Observable<NewsletterSectionResponseModel> {
        return this.http.post<NewsletterSectionResponseModel>(this.base_url + '/savesection', model)
            .pipe(catchError(this.authService.handleError));
    }

    addBlock(parentId: string, type: string, newsId: string): Observable<NewsletterSectionResponseModel> {
        return this.http.get<NewsletterSectionResponseModel>(this.base_url
            + '/addblock?newsId=' + newsId
            + '&parentId=' + parentId
            + '&sectionType=' + type)
            .pipe(catchError(this.authService.handleError));
    }

    saveBlock(model: NewsletterSection): Observable<NewsletterSectionResponseModel> {
        return this.http.post<NewsletterSectionResponseModel>(this.base_url + '/saveblock', model)
            .pipe(catchError(this.authService.handleError));
    }

    addLink(parentId: string, newsId: string): Observable<NewsletterSectionResponseModel> {
        return this.http.get<NewsletterSectionResponseModel>(this.base_url
            + '/addlink?newsId=' + newsId
            + '&parentId=' + parentId)
            .pipe(catchError(this.authService.handleError));
    }

    saveLink(model: NewsletterSection): Observable<NewsletterSectionResponseModel> {
        return this.http.post<NewsletterSectionResponseModel>(this.base_url + '/savelink', model)
            .pipe(catchError(this.authService.handleError));
    }

    addProducts(parentId: string, newsId: string): Observable<NewsletterSectionResponseModel> {
        return this.http.get<NewsletterSectionResponseModel>(this.base_url
            + '/addproducts?newsId=' + newsId
            + '&parentId=' + parentId)
            .pipe(catchError(this.authService.handleError));
    }

    getProducts(newsId: string, sectionId: string): Observable<NewsletterSectionResponseModel> {
        return this.http.get<NewsletterSectionResponseModel>(this.base_url + '/getproducts?newsId=' + newsId + '&sectionId=' + sectionId)
            .pipe(catchError(this.authService.handleError));
    }

}
